import { IPlayerGame } from "@grassrootly/api/entities";
import withErrorCheck from "../CheckError";
import server from "../Server";
import { GetPlayerFeaturedGamePayload } from "@grassrootly/api/gateways";

export const GetPlayerFeaturedGame = async ({
  pid,
}: GetPlayerFeaturedGamePayload = {}): Promise<IPlayerGame | null> => {
  if (!pid) return null;

  // Calculate 18 hours ago from now
  const eighteenHoursAgo = new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString();
  const now = new Date().toISOString();

  const response = await server
    .from("player_games_view")
    .select([
      "group_name",
      "group_logo",
      "game_id",
      "game_type",
      "game_status",
      "game_datetime",
      "game_host_team_name",
      "game_visitor_team_name",
      "game_host_score",
      "game_visitor_score",
      "player_id",
      "player_attended",
      "player_rating",
      "player_outcome",
      "player_goals_scored",
      "player_assists_made",
      "player_position",
      "player_yellow_cards",
      "player_red_cards",
      "player_saves",
      "player_own_goals",
      "player_errors_leading_to_a_goal",
      "organization_logo",
      "season_summary",
      "host_team_logo",
      "visitor_team_logo",
    ].join(","))
    .eq("player_id", pid)
    // Only look at games in the past 18 hours window (no future games)
    .gte("game_datetime", eighteenHoursAgo)
    .lt("game_datetime", now)
    .order("game_datetime", { ascending: false });

  const games = withErrorCheck<IPlayerGame[]>(response as any) || [];

  // First, look for past games (before now) that the player attended within the window
  const pastAttendedGames = games.filter(
    (game) => game.game_datetime && game.game_datetime < now && game.player_attended === true
  );

  if (pastAttendedGames.length > 0) {
    // Return the most recent past game that player attended
    const featuredGame = pastAttendedGames[0];
    featuredGame.player_id = pid;
    return featuredGame;
  }

  // If no past attended game is found, fall back to the upcoming group games view
  type UpcomingRow = {
    group_name: string;
    group_logo?: string | null;
    game_id: number;
    game_type: string;
    game_status: string;
    game_datetime: string | null;
    game_host_team_name: string;
    game_visitor_team_name: string;
    player_id: number;
    player_attended: boolean | null;
    organization_logo?: string | null;
    season_summary?: string | null;
    host_team_logo?: string | null;
    visitor_team_logo?: string | null;
  };

  const upcomingResponse = await server
    .from("player_upcoming_group_games_view")
    .select(
      [
        "group_name",
        "group_logo",
        "game_id",
        "game_type",
        "game_status",
        "game_datetime",
        "game_host_team_name",
        "game_visitor_team_name",
        "player_id",
        "player_attended",
        "organization_logo",
        "season_summary",
        "host_team_logo",
        "visitor_team_logo",
      ].join(",")
    )
    .eq("player_id", pid)
    .order("game_datetime", { ascending: true })
    .limit(1);

  const upcomingGames = withErrorCheck<UpcomingRow[]>(upcomingResponse as any) || [];
  if (!upcomingGames.length) {
    return null;
  }

  const upcoming = upcomingGames[0];

  // Map the upcoming-game subset into an IPlayerGame, with sensible defaults
  const featuredUpcoming: IPlayerGame = {
    group_name: upcoming.group_name,
    group_logo: upcoming.group_logo ?? undefined,
    game_id: upcoming.game_id,
    game_status: upcoming.game_status as any,
    game_location: "", // not available in view
    game_datetime: upcoming.game_datetime,
    player_attended: upcoming.player_attended,
    game_host_score: null,
    game_visitor_score: null,
    player_rating: 0,
    player_outcome: "",
    player_id: pid,
    player_goals_scored: 0,
    player_assists_made: 0,
    player_position: null,
    player_yellow_cards: 0,
    player_red_cards: 0,
    player_saves: 0,
    player_own_goals: 0,
    player_errors_leading_to_a_goal: 0,
    game_host_team_name: upcoming.game_host_team_name,
    game_visitor_team_name: upcoming.game_visitor_team_name,
    game_type: upcoming.game_type as any,
    organization_logo: upcoming.organization_logo ?? undefined,
    season_summary: upcoming.season_summary ?? undefined,
    host_team_logo: upcoming.host_team_logo ?? undefined,
    visitor_team_logo: upcoming.visitor_team_logo ?? undefined,
  };

  return featuredUpcoming;
};
